import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Space, Button } from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

interface GstTypeModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (data: any) => void; // callback after successful create/update
  isEditMode: boolean;
  initialValues: { gst_type_id: number; gst_type_name: string; gst_type_status: number } | null;
}

const GstTypeModal: React.FC<GstTypeModalProps> = ({
  visible,
  onCancel,
  onCreate,
  isEditMode,
  initialValues,
}) => {
  const [form] = Form.useForm();
  const [isModalClose, setIsModalClose] = useState(true);

  const isSubmitDisabled = () => {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0);
  };

  useEffect(() => {
    if (isEditMode && initialValues) {
      form.setFieldsValue({
        gst_type_name: initialValues.gst_type_name,
      });
    } else {
      form.resetFields();
    }
  }, [isEditMode, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const dataVal = {
        gst_type_name: values.gst_type_name,
        ...(initialValues?.gst_type_id && { gst_type_id: initialValues.gst_type_id }),
      };
      const url = isEditMode ? 'edit-gst-type' : 'add-gst-type';
      const method = isEditMode ? 'put' : 'post';

      const data = await apiRequest({
        method: method,
        url: url,
        data: dataVal,
      });

      showSuccess(isEditMode ? 'GST type updated successfully' : 'GST type created successfully!');
      form.resetFields();
      onCreate(data); // refresh table data
      onCancel(); // close modal
    } catch (error: any) {
      if (error.errorFields) {
        // validation error, do nothing
      } else {
        showError('Failed to save GST type');
        console.error(error);
      }
    }
  };

  return (
    <Modal
      open={visible}
      title={isEditMode ? 'Edit GST Type' : 'Add GST Type'}
      okText={isEditMode ? 'Update' : 'Create'}
      onCancel={onCancel}
      onOk={handleSubmit}
      destroyOnHidden
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        name="gst_type_form"
        onFinish={handleSubmit}
      >
        <Form.Item
          label="GST Type Name"
          name="gst_type_name"
          rules={[{ required: true, message: 'Please enter GST type name' }]}
        >
          <Input showCount maxLength={50} placeholder="Enter GST type name" />
        </Form.Item>
        <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            {!isEditMode && (
              <Button
                type="primary"
                htmlType="submit"
                onClick={() => setIsModalClose(false)}
                disabled={isSubmitDisabled()}
              >
                Submit & Create New
              </Button>
            )}
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => setIsModalClose(true)}
              disabled={isSubmitDisabled()}
            >
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default GstTypeModal;
